import React, { useMemo, useState } from "react";
import { Box, Typography, Stack, Autocomplete, TextField, Chip } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridToolbarQuickFilter,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { getInventoryQuery } from "../../lib/Queries";
import { InventoryItem } from "../../lib/Types";
import Loading from "../Shared/Loading";
import DataComponentError from "../Shared/DataComponentError";
import { useTheme } from "@mui/material/styles";

const styles = {
  container: { p: 2, pl: 4, pr: 2, width: "100%" },
} as const;

const columns: GridColDef[] = [
  { field: "site_name", headerName: "Site", width: 100 },
  { field: "asset_name", headerName: "Asset", width: 220 },
  { field: "asset_type", headerName: "Asset Type", width: 120 },
  { field: "key", headerName: "Key", width: 220 },
  { field: "value", headerName: "Value", flex: 1, minWidth: 220 },
];

const getRows = (items: InventoryItem[]) => {
  return items.map((item, index) => ({ id: index, ...item }));
};

const Inventory: React.FC = () => {
  const { data, isLoading, isError } = useQuery(getInventoryQuery());
  const theme = useTheme() as any;

  const rows = getRows(data || []);

  // Filter state
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState<string[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);

  // Derive filter options from data
  const siteOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r: any) => {
      if (r.site_name) set.add(r.site_name);
    });
    return Array.from(set).sort();
  }, [rows]);

  const assetTypeOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r: any) => {
      if (r.asset_type) set.add(r.asset_type);
    });
    return Array.from(set).sort();
  }, [rows]);

  const assetOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r: any) => {
      if (r.asset_name) set.add(r.asset_name);
    });
    return Array.from(set).sort();
  }, [rows]);

  // Apply filters
  const filteredRows = useMemo(() => {
    return rows.filter((r: any) => {
      const siteOk = selectedSites.length === 0 || selectedSites.includes(r.site_name || "");
      const typeOk =
        selectedAssetTypes.length === 0 || selectedAssetTypes.includes(r.asset_type || "");
      const assetOk = selectedAssets.length === 0 || selectedAssets.includes(r.asset_name || "");
      return siteOk && typeOk && assetOk;
    });
  }, [rows, selectedSites, selectedAssetTypes, selectedAssets]);

  const getSelector = (
    label: string,
    options: string[],
    value: string[],
    setValue: (val: string[]) => void,
  ) => {
    return (
      <Autocomplete
        multiple
        options={options}
        value={value}
        onChange={(_, newValue) => setValue(newValue)}
        size="small"
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option: string, index: number) => (
            <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => <TextField {...params} label={label} placeholder={label} />}
        sx={{ minWidth: 200 }}
      />
    );
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Stack direction="row" spacing={2} alignItems="start" sx={{ width: "100%", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1, flexWrap: "wrap" }}>
          {getSelector("Site", siteOptions, selectedSites, setSelectedSites)}
          {getSelector("Asset Type", assetTypeOptions, selectedAssetTypes, setSelectedAssetTypes)}
          {getSelector("Asset", assetOptions, selectedAssets, setSelectedAssets)}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <GridToolbarQuickFilter placeholder="Search all" />
        </Box>
      </Stack>
    </GridToolbarContainer>
  );

  if (isLoading) return <Loading />;
  if (isError) return <DataComponentError />;

  return (
    <Box sx={theme.classes.pageContainer}>
      <Box sx={theme.classes.page}>
        <Typography sx={{ mb: "1.5rem", fontSize: "24px", fontWeight: 600 }} variant="h5">
          Inventory
        </Typography>
        <Box sx={theme.classes.genericPanel}>
          <Box sx={styles.container}>
            <Box style={{ ...theme.classes.tableContainer }}>
              <DataGrid
                rows={filteredRows}
                columns={columns}
                disableRowSelectionOnClick
                hideFooterRowCount
                density="compact"
                autoHeight
                slots={{ toolbar: CustomToolbar }}
                sx={{
                  "&, .MuiDataGrid-columnHeader": {
                    borderTop: "none",
                  },
                  "&, [class^=MuiDataGrid]": {
                    overflowX: "hidden",
                    borderLeft: "none",
                    borderRight: "none",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Inventory;
